import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import { Grid, Box } from "@mui/material";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import { GHG_EMISSIONS_MANAGEMENT_FORM } from "data/forms/griFormSchema";
import {
  emissionsTracking,
  emissionsMethodologyChecklist,
  realTimeEmissionCalculation,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import { EMISSION_METHODOLOGY_CHECKLIST } from "data/standards/griStandards";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";

const EmissionTracking = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  // Force re-render of components when step changes
  const [forceUpdate, setForceUpdate] = useState(0);

  const steps = [
    "Emissions Tracking Setup",
    "Methodology Selection",
    "Organization Information",
    "Upload Evidence & Data",
    "Real-time Emissions Calculation",
    "Upload Evidence and Review Reports",
    "Download Gap Assessment",
    "Upload Gap Form",
    "Final Reports",
  ];

  useEffect(() => {
    if (activeStep === 5 && prevStep === 4) {
      setShowReviewPopup(true);
    }
    setForceUpdate((prev) => prev + 1);
  }, [activeStep, prevStep]);

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(6);
    setShowReviewPopup(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            key="emissions-tracking-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={emissionsTracking}
            title="GHG Emissions Tracking Setup"
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
            ReportDownloadButtonShow={false}
          />
        );

      case 1:
        return (
          <StandardsSelector
            standards={EMISSION_METHODOLOGY_CHECKLIST}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Methodology & Standards Selection"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 2:
        return (
          <ReusableFormComponent
            {...GHG_EMISSIONS_MANAGEMENT_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 3:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
            title="Upload GHG Data & Evidence"
            description="Upload your emissions data, energy consumption logs, and supporting documentation"
          />
        );
      case 4:
        return (
          <DownloadReport
            key="realtime-emissionsCalculation-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={realTimeEmissionCalculation}
            title="GHG Emissions Tracking Setup"
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );
      case 5:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 6:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
            title="Gap Assessment Form"
            description="Download gap assessment form to identify missing data or improvement areas"
          />
        );
      case 7:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
            title="Upload Completed Gap Assessment"
            description="Upload your completed gap assessment for final analysis"
          />
        );
      case 8:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            title="Final GHG Reports"
            description="Download your complete GHG emissions reports and regulatory submissions"
            showReportCountdownButton={true}
            standardName="GHG Emissions"
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader
              title="GHG Emissions Management & Reporting System"
              subtitle="AI-Powered greenhouse gas emissions tracking, calculation, and regulatory reporting"
            />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg2_1"
                title="GHG Emissions Management"
                activeStep={activeStep}
                onBack={handleBack}
                steps={steps}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box key={`step-content-${activeStep}-${forceUpdate}`}>
            {getStepContent(activeStep)}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          >
            {/* <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                                Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                            </Box>
                        </Box> */}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default EmissionTracking;
